import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { AlertTriangle, X } from 'lucide-react'
import {
  calculateWorkingDays,
  listHolidaysInRange,
  VACATION_PAYMENT_DISCLAIMER,
} from '@czas/shared'

import Button from '@/components/ui/Button'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'

import { useAdminUsers } from '@/features/admin/use-admin-users'
import {
  useAdminCreateVacationRequest,
  useAdminVacationBalances,
} from '@/features/admin/use-admin-vacation'
import PaymentBadge from '@/features/vacation/payment-badge'
import type { VacationType } from '@/features/vacation/use-vacation'

const TYPE_LABELS: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const schema = z
  .object({
    userId: z.string().min(1, 'Wybierz pracownika'),
    type: z.enum([
      'WYPOCZYNKOWY',
      'NA_ZADANIE',
      'BEZPLATNY',
      'CHOROBOWY',
      'OPIEKA_NAD_DZIECKIEM',
    ]),
    startDate: z.string().min(1, 'Wybierz datę rozpoczęcia'),
    endDate: z.string().min(1, 'Wybierz datę zakończenia'),
    comment: z.string().max(500, 'Maksymalnie 500 znaków').optional(),
  })
  .refine((v) => v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'Data zakończenia musi być nie wcześniejsza niż rozpoczęcia',
  })

type FormValues = z.infer<typeof schema>

function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(Date.UTC(y!, m! - 1, d!))
}

function formatHumanDate(iso: string): string {
  return format(parseIsoDate(iso), 'd MMMM', { locale: pl })
}

interface RangePreview {
  workingDays: number
  holidays: { date: string; name: string }[]
  weekendCount: number
}

function computeRangePreview(startIso: string, endIso: string): RangePreview {
  const start = parseIsoDate(startIso)
  const end = parseIsoDate(endIso)
  if (end < start) return { workingDays: 0, holidays: [], weekendCount: 0 }
  const wd = calculateWorkingDays(start, end)
  const holidays = listHolidaysInRange(start, end)
  let weekendCount = 0
  const current = new Date(start.getTime())
  while (current.getTime() <= end.getTime()) {
    const dow = current.getUTCDay()
    if (dow === 0 || dow === 6) weekendCount++
    current.setUTCDate(current.getUTCDate() + 1)
  }
  return { workingDays: wd, holidays, weekendCount }
}

interface DialogProps {
  open: boolean
  onClose: () => void
}

export default function AdminVacationCreateDialog({ open, onClose }: DialogProps) {
  const create = useAdminCreateVacationRequest()
  const usersQuery = useAdminUsers({ active: 'true' })

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      userId: '',
      type: 'CHOROBOWY',
      startDate: '',
      endDate: '',
      comment: '',
    },
  })

  const watchedUserId = form.watch('userId')
  const watchedType = form.watch('type')
  const watchedStart = form.watch('startDate')
  const watchedEnd = form.watch('endDate')

  const year = useMemo(() => {
    if (!watchedStart) return new Date().getFullYear()
    return parseIsoDate(watchedStart).getUTCFullYear()
  }, [watchedStart])

  const balancesQuery = useAdminVacationBalances(year)

  useEffect(() => {
    if (open) {
      form.reset({
        userId: '',
        type: 'CHOROBOWY',
        startDate: '',
        endDate: '',
        comment: '',
      })
    }
  }, [open, form])

  const preview = useMemo(() => {
    if (!watchedStart || !watchedEnd) {
      return { workingDays: 0, holidays: [], weekendCount: 0 }
    }
    return computeRangePreview(watchedStart, watchedEnd)
  }, [watchedStart, watchedEnd])

  const quotaWarning = useMemo(() => {
    if (!watchedUserId || preview.workingDays === 0) return null
    const entry = balancesQuery.data?.balances.find((b) => b.user.id === watchedUserId)
    if (!entry) return null

    if (watchedType === 'OPIEKA_NAD_DZIECKIEM') {
      const remaining = entry.byType.OPIEKA_NAD_DZIECKIEM.remainingPerYear
      if (preview.workingDays > remaining) {
        return `Limit „opieka nad dzieckiem": ${remaining} z ${entry.byType.OPIEKA_NAD_DZIECKIEM.maxPerYear} dni — wniosek zajmuje ${preview.workingDays}.`
      }
      return null
    }

    if (watchedType !== 'WYPOCZYNKOWY' && watchedType !== 'NA_ZADANIE') return null

    if (watchedType === 'NA_ZADANIE') {
      const remaining = entry.byType.NA_ZADANIE.remainingPerYear
      if (preview.workingDays > remaining) {
        return `Limit „na żądanie": ${remaining} z ${entry.byType.NA_ZADANIE.maxPerYear} dni — wniosek zajmuje ${preview.workingDays}.`
      }
    }

    if (preview.workingDays > entry.balance.remainingDays) {
      return `Pula urlopu: pozostało ${entry.balance.remainingDays} z ${entry.balance.totalDays} dni — wniosek zajmuje ${preview.workingDays}.`
    }
    return null
  }, [watchedUserId, watchedType, preview.workingDays, balancesQuery.data])

  async function onSubmit(values: FormValues) {
    if (preview.workingDays === 0) {
      toast.error('Zakres nie zawiera żadnego dnia roboczego.')
      return
    }
    try {
      const result = await create.mutateAsync({
        userId: values.userId,
        type: values.type,
        startDate: values.startDate,
        endDate: values.endDate,
        comment: values.comment?.trim() ? values.comment.trim() : undefined,
      })
      toast.success(`Urlop wpisany dla ${result.request.user.name}`)
      if (result.quotaWarning) {
        toast.warning(result.quotaWarning)
      }
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-vacation-create-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-border-subtle">
          <h2 id="admin-vacation-create-title" className="text-lg font-semibold">
            Wpisz urlop dla pracownika
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-text-primary hover:bg-bg-surface transition"
            aria-label="Zamknij"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          className="px-6 py-5 space-y-4"
        >
          <div>
            <Label htmlFor="admin-vac-user">Pracownik</Label>
            <select
              id="admin-vac-user"
              className={cn(
                'h-10 w-full rounded-action border bg-bg-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40',
                form.formState.errors.userId
                  ? 'border-danger/60'
                  : 'border-border-strong',
              )}
              {...form.register('userId')}
            >
              <option value="">— Wybierz pracownika —</option>
              {(usersQuery.data?.users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                  {u.position ? ` · ${u.position.name}` : ''}
                </option>
              ))}
            </select>
            {form.formState.errors.userId && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.userId.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label htmlFor="admin-vac-type" className="mb-0">
                Typ urlopu
              </Label>
              <PaymentBadge type={watchedType} />
            </div>
            <select
              id="admin-vac-type"
              className="h-10 w-full rounded-action border border-border-strong bg-bg-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              {...form.register('type')}
            >
              {(
                [
                  'CHOROBOWY',
                  'WYPOCZYNKOWY',
                  'NA_ZADANIE',
                  'BEZPLATNY',
                  'OPIEKA_NAD_DZIECKIEM',
                ] as const
              ).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="admin-vac-start">Data początkowa</Label>
              <input
                id="admin-vac-start"
                type="date"
                className="h-10 w-full rounded-action border border-border-strong bg-bg-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                {...form.register('startDate')}
              />
              {form.formState.errors.startDate && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.startDate.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="admin-vac-end">Data końcowa</Label>
              <input
                id="admin-vac-end"
                type="date"
                className="h-10 w-full rounded-action border border-border-strong bg-bg-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                {...form.register('endDate')}
              />
              {form.formState.errors.endDate && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.endDate.message}
                </p>
              )}
            </div>
          </div>

          {watchedStart && watchedEnd && watchedEnd >= watchedStart && (
            <RangePreviewSection preview={preview} />
          )}

          <div>
            <Label htmlFor="admin-vac-comment">
              Komentarz <span className="text-text-faint text-xs">(opcjonalny)</span>
            </Label>
            <textarea
              id="admin-vac-comment"
              rows={3}
              maxLength={500}
              placeholder="Np. dla CHOROBOWY: nr L4 wystawiony przez dr X."
              className="w-full rounded-action border border-border-strong bg-bg-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              {...form.register('comment')}
            />
          </div>

          {quotaWarning && (
            <div className="rounded-card border border-warning/30 bg-warning/5 text-warning px-3 py-2 text-sm">
              <AlertTriangle className="inline-block h-4 w-4 mr-1 align-text-bottom" aria-hidden />
              {quotaWarning} Mimo to wpis będzie zapisany.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={preview.workingDays === 0}
            >
              Wpisz urlop
            </Button>
          </div>
          <p className="text-[11px] text-text-faint text-center pt-1">
            {VACATION_PAYMENT_DISCLAIMER}
          </p>
        </form>
      </div>
    </div>
  )
}

function RangePreviewSection({ preview }: { preview: RangePreview }) {
  return (
    <div className="rounded-card border border-border-subtle bg-bg-surface px-3 py-3 text-sm space-y-2">
      <div className="text-text-primary">
        <span className="font-medium">{preview.workingDays}</span>{' '}
        {preview.workingDays === 1 ? 'dzień roboczy' : 'dni roboczych'} w wybranym zakresie
      </div>
      {(preview.holidays.length > 0 || preview.weekendCount > 0) && (
        <div className="text-text-muted">
          <div className="text-xs uppercase tracking-wider text-text-faint mb-1">
            W zakresie wolne dni
          </div>
          <ul className="space-y-0.5">
            {preview.holidays.map((h) => (
              <li key={h.date}>
                {formatHumanDate(h.date)} ({h.name})
              </li>
            ))}
            {preview.weekendCount > 0 && (
              <li>{preview.weekendCount} dni weekendowych</li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
